import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Form,
  Modal,
  Spinner,
  Table,
  Toast,
  ToastContainer,
} from 'react-bootstrap';

import {
  createVideoTemplate,
  deleteVideoTemplate,
  fetchVideoTemplates,
  parseVideoTemplateSlots,
  syncOrdinaryVideoTemplate,
  syncVideoTemplate,
  updateVideoTemplate,
} from '@/features/automation/videoEditing/services/templateService.js';
import { fetchVideoMaterials } from '@/features/automation/videoEditing/services/materialService.js';
import { getVideoTypes } from '@/features/systemManagement/videoType/videoTypeService.js';
import {
  ALIYUN_BUILT_IN_FONT_OPTIONS,
  VIDEO_PRODUCTION_SLOT_KIND,
} from '@/features/automation/videoEditing/constants.js';
import {
  buildTemplateSlotEditorEntries,
  createTemplateSlotGroupFromSelection,
  getTemplateSlotEditorEntryId,
  getTextFitRuntimeValidationError,
  normalizeTemplateSlots,
  normalizeSlotGroupConfig,
  getTemplateSlotKindLabel,
  isVideoTemplatePageBusy,
  moveTemplateSlotEditorEntry,
  serializeSlotGroupConfig,
  serializeTemplateSlots,
  splitTemplateSlotGroups,
  TEMPLATE_SLOT_EDITOR_ENTRY_TYPE,
  TEXT_FIT_RUNTIME_KEYS,
  shouldShowParseSlotsButton,
  shouldShowRefreshAdvancedTemplateButton,
  shouldShowSyncOrdinaryTemplateButton,
} from '@/features/automation/videoEditing/utils/templateSlotUtils.js';
import {
  normalizeMaterialFontAssets,
} from '@/features/automation/videoEditing/utils/productionSlotUtils.js';
import LoadingIndicator from '@/components/LoadingIndicator.js';
import PageContentLayout from '@/components/layout/PageContentLayout/PageContentLayout.js';
import {
  VerticalSortableList,
  VerticalSortableScrollArea,
} from '@/components/sortable/VerticalSortableList.js';
import {
  getVideoTemplateStatusLabel,
  getVideoTemplateTypeLabel,
} from '@/features/automation/videoEditing/utils/templateStatus.js';
import {
  SortableGroupRow,
  SortableSlotRow,
} from './components/TemplateSlotEditorRows.js';
import './VideoTemplatesPage.scss';

const SLOT_TABLE_COLUMN_COUNT = 5;
const TEMPLATE_EDIT_DIALOG_CLASS = 'video-template-edit-dialog';
const SLOT_EDITOR_ACTION_TEXT = {
  MERGE_FALLBACK: '合并为组',
  SPLIT_SELECTED_GROUPS: '拆分选中组',
};
const POSITIVE_INTEGER_RUNTIME_KEYS = new Set([
  ...TEXT_FIT_RUNTIME_KEYS,
  'max_length',
]);

const normalizeSlotConfigValue = (configKey, value) => {
  if (value === undefined || value === null || value === '') return undefined;
  if (POSITIVE_INTEGER_RUNTIME_KEYS.has(configKey)) {
    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
  }
  if (configKey === 'adapt_type') {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? undefined : parsed;
  }
  return String(value).trim();
};

const normalizeVideoTypeIds = (value = []) =>
  (Array.isArray(value) ? value : [value])
    .map((videoTypeId) => Number(videoTypeId))
    .filter((videoTypeId) => Number.isInteger(videoTypeId) && videoTypeId > 0);

const getVideoTypeLabel = (videoType) =>
  videoType.title || videoType.videoType || videoType.name || `视频标准 ${videoType.id}`;

const VideoTemplatesPage = () => {
  const [templates, setTemplates] = useState([]);
  const [videoTypes, setVideoTypes] = useState([]);
  const [fontMaterials, setFontMaterials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [savingId, setSavingId] = useState('');
  const [deletingId, setDeletingId] = useState('');
  const [parsingId, setParsingId] = useState('');
  const [syncingId, setSyncingId] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [deleteCandidate, setDeleteCandidate] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedVideoTypeIds, setSelectedVideoTypeIds] = useState([]);
  const [slotRows, setSlotRows] = useState([]);
  const [slotGroupConfig, setSlotGroupConfig] = useState({});
  const [selectedSlotKeys, setSelectedSlotKeys] = useState([]);
  const [selectedGroupKeys, setSelectedGroupKeys] = useState([]);
  const [expandedGroupKeys, setExpandedGroupKeys] = useState([]);
  const [createForm, setCreateForm] = useState({
    name: '',
    file: null,
  });
  const [syncForm, setSyncForm] = useState({
    remote_template_id: '',
  });
  const [editForm, setEditForm] = useState({
    name: '',
    is_active: true,
  });
  const [toastInfo, setToastInfo] = useState({
    show: false,
    message: '',
    variant: 'success',
  });

  const showToast = useCallback((message, variant = 'success') => {
    setToastInfo({ show: true, message, variant });
  }, []);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const [templateData, materialData, videoTypeData] = await Promise.all([
        fetchVideoTemplates(),
        fetchVideoMaterials({ is_active: true, limit: 200 }).catch(() => []),
        getVideoTypes(),
      ]);
      setTemplates(templateData);
      setFontMaterials(materialData);
      setVideoTypes(videoTypeData || []);
    } catch (error) {
      showToast(error.response?.data?.message || '获取自动化模板失败', 'danger');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  useEffect(() => {
    if (!selectedTemplate) return;
    setEditForm({
      name: selectedTemplate.name || '',
      is_active: !!selectedTemplate.is_active,
    });
    setSlotRows(normalizeTemplateSlots(selectedTemplate.slot_config));
    setSlotGroupConfig(normalizeSlotGroupConfig(selectedTemplate.slot_group_config));
    setSelectedSlotKeys([]);
    setSelectedGroupKeys([]);
    setExpandedGroupKeys([]);
    setSelectedVideoTypeIds(normalizeVideoTypeIds(selectedTemplate?.video_type_ids));
  }, [selectedTemplate]);

  const selectedStatus = useMemo(
    () => getVideoTemplateStatusLabel(selectedTemplate?.status),
    [selectedTemplate]
  );

  const pageBusy = isVideoTemplatePageBusy({
    loading,
    creating,
    savingId,
    deletingId,
    parsingId,
    syncingId,
  });

  const pageBusyLabel =
    (creating && '高级模板创建中') ||
    (parsingId && '插槽解析中') ||
    (syncingId && '模板同步中') ||
    (savingId && '模板保存中') ||
    (deletingId && '模板删除中') ||
    '加载中';
  const slotEditorEntries = useMemo(
    () =>
      buildTemplateSlotEditorEntries({
        slots: slotRows,
        groupConfig: slotGroupConfig,
        expandedGroupKeys,
      }),
    [slotRows, slotGroupConfig, expandedGroupKeys]
  );
  const slotSortableIds = useMemo(
    () => slotEditorEntries.map(getTemplateSlotEditorEntryId).filter(Boolean),
    [slotEditorEntries]
  );
  const selectedSlotKeySet = useMemo(
    () => new Set(selectedSlotKeys),
    [selectedSlotKeys]
  );
  const selectedGroupKeySet = useMemo(
    () => new Set(selectedGroupKeys),
    [selectedGroupKeys]
  );
  const selectedSlots = useMemo(
    () => slotRows.filter((slot) => selectedSlotKeySet.has(slot.key)),
    [slotRows, selectedSlotKeySet]
  );
  const selectedSlotKinds = useMemo(
    () => Array.from(new Set(selectedSlots.map((slot) => slot.kind))),
    [selectedSlots]
  );
  const mergeSelectedSlotsLabel = useMemo(() => {
    if (selectedSlotKinds.length !== 1) return SLOT_EDITOR_ACTION_TEXT.MERGE_FALLBACK;
    return `合并为${getTemplateSlotKindLabel(selectedSlotKinds[0])}组`;
  }, [selectedSlotKinds]);
  const canMergeSelectedSlots = selectedSlotKeys.length >= 2;
  const canSplitSelectedGroups = selectedGroupKeys.length > 0;
  const customFontOptions = useMemo(
    () =>
      normalizeMaterialFontAssets(fontMaterials).map((font) => ({
        value: font.value,
        label: `自定义：${font.label}`,
      })),
    [fontMaterials]
  );
  const fontOptions = useMemo(() => {
    const options = [
      ...ALIYUN_BUILT_IN_FONT_OPTIONS,
      ...customFontOptions,
    ];
    const seen = new Set();
    return options.filter((option) => {
      if (!option.value || seen.has(option.value)) return false;
      seen.add(option.value);
      return true;
    });
  }, [customFontOptions]);

  const closeCreateModal = () => {
    if (pageBusy) return;
    setShowCreateModal(false);
    setCreateForm({ name: '', file: null });
  };

  const closeSyncModal = () => {
    if (pageBusy) return;
    setShowSyncModal(false);
    setSyncForm({ remote_template_id: '' });
  };

  const closeEditModal = () => {
    if (pageBusy) return;
    setShowEditModal(false);
    setSelectedTemplate(null);
    setSelectedVideoTypeIds([]);
    setSlotGroupConfig({});
    setSelectedSlotKeys([]);
    setSelectedGroupKeys([]);
    setExpandedGroupKeys([]);
  };

  const closeDeleteModal = () => {
    if (pageBusy) return;
    setDeleteCandidate(null);
  };

  const handleCreateChange = (event) => {
    const { name, value, files } = event.target;
    setCreateForm((current) => ({
      ...current,
      [name]: files ? files[0] || null : value,
    }));
  };

  const handleSyncChange = (event) => {
    const { name, value } = event.target;
    setSyncForm((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleCreateTemplate = async (event) => {
    event.preventDefault();

    if (pageBusy) return;

    const templateName = createForm.name.trim();
    if (!templateName) {
      showToast('请输入模板名称', 'danger');
      return;
    }

    if (!createForm.file) {
      showToast('请选择模板 ZIP 文件', 'danger');
      return;
    }

    const payload = new FormData();
    payload.append('name', templateName);
    payload.append('template_file', createForm.file);

    setCreating(true);
    try {
      await createVideoTemplate(payload);
      setCreateForm({ name: '', file: null });
      setShowCreateModal(false);
      await loadTemplates();
      showToast('高级模板已创建，阿里云解析完成后会自动更新状态');
    } catch (error) {
      showToast(error.response?.data?.message || '创建模板失败', 'danger');
    } finally {
      setCreating(false);
    }
  };

  const upsertTemplateInState = (updated) => {
    setTemplates((current) => {
      const exists = current.some((item) => item.id === updated.id);
      if (!exists) return [updated, ...current];
      return current.map((item) => (item.id === updated.id ? updated : item));
    });
    setSelectedTemplate((current) =>
      current?.id === updated.id ? updated : current
    );
  };

  const handleToggleTemplateActive = async (template) => {
    if (!template || pageBusy) return;

    const nextIsActive = !(template.is_active !== false);
    setSavingId(template.id);
    try {
      const updated = await updateVideoTemplate(template.id, {
        name: template.name,
        is_active: nextIsActive,
        slot_config: template.slot_config || [],
      });
      upsertTemplateInState(updated);
      showToast(nextIsActive ? '模板已启用' : '模板已停用');
    } catch (error) {
      showToast(error.response?.data?.message || '更新模板启用状态失败', 'danger');
    } finally {
      setSavingId('');
    }
  };

  const handleSyncOrdinaryTemplate = async (event) => {
    event.preventDefault();
    if (pageBusy) return;

    const remoteTemplateId = syncForm.remote_template_id.trim();
    if (!remoteTemplateId) {
      showToast('请输入阿里云普通模板 ID', 'danger');
      return;
    }

    setSyncingId('ordinary-template-sync');
    try {
      const template = await syncOrdinaryVideoTemplate(remoteTemplateId);
      upsertTemplateInState(template);
      setSyncForm({ remote_template_id: '' });
      setShowSyncModal(false);
      setSelectedTemplate(template);
      setShowEditModal(true);
      showToast('普通模板已同步');
    } catch (error) {
      showToast(error.response?.data?.message || '同步普通模板失败', 'danger');
    } finally {
      setSyncingId('');
    }
  };

  const handleSyncTemplate = async (template) => {
    if (!template || pageBusy) return;

    setSyncingId(template.id);
    try {
      const updated = await syncVideoTemplate(template.id);
      upsertTemplateInState(updated);
      showToast('模板状态已同步');
    } catch (error) {
      showToast(error.response?.data?.message || '同步模板失败', 'danger');
    } finally {
      setSyncingId('');
    }
  };

  const handleParseSlots = async (template) => {
    if (!template || pageBusy) return;

    setParsingId(template.id);
    try {
      const updated = await parseVideoTemplateSlots(template.id);
      upsertTemplateInState(updated);
      if (selectedTemplate?.id === updated.id) {
        setSlotRows(normalizeTemplateSlots(updated.slot_config));
      }
      showToast('插槽已解析');
    } catch (error) {
      showToast(error.response?.data?.message || '解析插槽失败', 'danger');
    } finally {
      setParsingId('');
    }
  };

  const handleSelectTemplate = (template) => {
    if (pageBusy) return;
    setSelectedTemplate(template);
    setShowEditModal(true);
  };

  const handleDeleteTemplate = async () => {
    if (!deleteCandidate || pageBusy) return;

    const templateId = deleteCandidate.id;
    setDeletingId(templateId);
    try {
      await deleteVideoTemplate(templateId);
      setTemplates((current) =>
        current.filter((template) => template.id !== templateId)
      );
      setSelectedTemplate((current) =>
        current?.id === templateId ? null : current
      );
      if (selectedTemplate?.id === templateId) {
        setShowEditModal(false);
      }
      setDeleteCandidate(null);
      showToast('模板已删除');
    } catch (error) {
      showToast(error.response?.data?.message || '删除模板失败', 'danger');
    } finally {
      setDeletingId('');
    }
  };

  const handleEditChange = (event) => {
    const { name, value, checked, type } = event.target;
    setEditForm((current) => ({
      ...current,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSlotChange = (index, field, value) => {
    setSlotRows((current) =>
      current.map((slot, slotIndex) =>
        slotIndex === index ? { ...slot, [field]: value } : slot
      )
    );
  };

  const handleSlotConfigChange = (index, configKey, value) => {
    setSlotRows((current) =>
      current.map((slot, slotIndex) => {
        if (slotIndex !== index) return slot;

        const nextConfig = { ...(slot.config || {}) };
        const normalizedValue = normalizeSlotConfigValue(configKey, value);
        if (normalizedValue === undefined || normalizedValue === '') {
          delete nextConfig[configKey];
        } else {
          nextConfig[configKey] = normalizedValue;
        }

        return { ...slot, config: nextConfig };
      })
    );
  };

  const handleSlotRuntimeChange = (index, runtimeKey, value) => {
    setSlotRows((current) =>
      current.map((slot, slotIndex) => {
        if (slotIndex !== index) return slot;

        const nextRuntime = { ...(slot.runtime || {}) };
        const normalizedValue = normalizeSlotConfigValue(runtimeKey, value);
        if (normalizedValue === undefined || normalizedValue === '') {
          delete nextRuntime[runtimeKey];
        } else {
          nextRuntime[runtimeKey] = normalizedValue;
        }

        return { ...slot, runtime: nextRuntime };
      })
    );
  };

  const handleSlotSelectionChange = (slotKey, checked) => {
    setSelectedSlotKeys((current) => {
      const next = new Set(current);
      if (checked) {
        next.add(slotKey);
      } else {
        next.delete(slotKey);
      }
      return Array.from(next);
    });
  };

  const handleGroupSelectionChange = (groupKey, checked) => {
    setSelectedGroupKeys((current) => {
      const next = new Set(current);
      if (checked) {
        next.add(groupKey);
      } else {
        next.delete(groupKey);
      }
      return Array.from(next);
    });
  };

  const handleMergeSelectedSlots = () => {
    try {
      const result = createTemplateSlotGroupFromSelection({
        slots: slotRows,
        groupConfig: slotGroupConfig,
        selectedSlotKeys,
      });
      setSlotRows(result.slots);
      setSlotGroupConfig(result.groupConfig);
      setExpandedGroupKeys((current) =>
        current.includes(result.groupKey)
          ? current
          : [...current, result.groupKey]
      );
      setSelectedSlotKeys([]);
      setSelectedGroupKeys([]);
    } catch (error) {
      showToast(error.message || '合并插槽失败', 'danger');
    }
  };

  const handleSplitSelectedGroups = () => {
    const result = splitTemplateSlotGroups({
      slots: slotRows,
      groupConfig: slotGroupConfig,
      groupKeys: selectedGroupKeys,
    });
    setSlotRows(result.slots);
    setSlotGroupConfig(result.groupConfig);
    setExpandedGroupKeys((current) =>
      current.filter((groupKey) => !selectedGroupKeySet.has(groupKey))
    );
    setSelectedGroupKeys([]);
  };

  const handleToggleGroupExpanded = (groupKey) => {
    setExpandedGroupKeys((current) =>
      current.includes(groupKey)
        ? current.filter((key) => key !== groupKey)
        : [...current, groupKey]
    );
  };

  const handleSlotGroupFieldChange = (groupKey, field, value) => {
    setSlotGroupConfig((current) => ({
      ...current,
      [groupKey]: {
        ...current[groupKey],
        [field]: value,
      },
    }));
  };

  const handleSlotGroupConfigChange = (groupKey, configKey, value) => {
    setSlotGroupConfig((current) => {
      const group = current[groupKey] || {};
      const nextConfig = { ...(group.config || {}) };
      const normalizedValue = normalizeSlotConfigValue(configKey, value);
      if (normalizedValue === undefined || normalizedValue === '') {
        delete nextConfig[configKey];
      } else {
        nextConfig[configKey] = normalizedValue;
      }
      return {
        ...current,
        [groupKey]: {
          ...group,
          config: nextConfig,
        },
      };
    });
  };

  const handleSlotGroupRuntimeChange = (groupKey, runtimeKey, value) => {
    setSlotGroupConfig((current) => {
      const group = current[groupKey] || {};
      const nextRuntime = { ...(group.runtime || {}) };
      const normalizedValue = normalizeSlotConfigValue(runtimeKey, value);
      if (normalizedValue === undefined || normalizedValue === '') {
        delete nextRuntime[runtimeKey];
      } else {
        nextRuntime[runtimeKey] = normalizedValue;
      }
      return {
        ...current,
        [groupKey]: {
          ...group,
          runtime: nextRuntime,
        },
      };
    });
  };

  const handleSlotMove = useCallback(
    ({ activeId, overId }) => {
      setSlotRows((current) =>
        moveTemplateSlotEditorEntry({
          slots: current,
          groupConfig: slotGroupConfig,
          expandedGroupKeys,
          activeId,
          overId,
        })
      );
    },
    [expandedGroupKeys, slotGroupConfig]
  );

  const handleVideoTypeToggle = (videoTypeId) => {
    const normalizedId = Number(videoTypeId);
    setSelectedVideoTypeIds((current) =>
      current.includes(normalizedId)
        ? current.filter((id) => id !== normalizedId)
        : [...current, normalizedId]
    );
  };

  const handleSaveConfig = async () => {
    if (!selectedTemplate || pageBusy) return;

    const templateName = editForm.name.trim();
    if (!templateName) {
      showToast('请输入模板名称', 'danger');
      return;
    }

    for (const slot of slotRows) {
      if (
        slot.kind !== VIDEO_PRODUCTION_SLOT_KIND.TEXT ||
        slot.group_key
      ) {
        continue;
      }
      const error = getTextFitRuntimeValidationError(
        slot.runtime,
        `插槽 ${slot.key}`
      );
      if (error) {
        showToast(error, 'danger');
        return;
      }
    }
    for (const [groupKey, group] of Object.entries(slotGroupConfig)) {
      if (group.kind !== VIDEO_PRODUCTION_SLOT_KIND.TEXT) continue;
      const error = getTextFitRuntimeValidationError(
        group.runtime,
        `分组 ${group.name || groupKey}`
      );
      if (error) {
        showToast(error, 'danger');
        return;
      }
    }

    const normalizedSlots = serializeTemplateSlots(slotRows);
    const normalizedGroups = serializeSlotGroupConfig(slotGroupConfig);

    if (normalizedSlots.some((slot) => !slot.key)) {
      showToast('插槽 Key 不能为空', 'danger');
      return;
    }

    const slotKeys = normalizedSlots.map((slot) => slot.key);
    if (new Set(slotKeys).size !== slotKeys.length) {
      showToast('插槽 Key 不能重复', 'danger');
      return;
    }

    for (const slot of normalizedSlots) {
      if (!slot.group_key) continue;
      const group = normalizedGroups[slot.group_key];
      if (!group) {
        showToast('插槽分组不存在', 'danger');
        return;
      }
      if (group.kind !== slot.kind) {
        showToast('插槽分组类型不匹配', 'danger');
        return;
      }
    }

    setSavingId(selectedTemplate.id);
    try {
      const updated = await updateVideoTemplate(selectedTemplate.id, {
        name: templateName,
        is_active: editForm.is_active,
        slot_config: normalizedSlots,
        slot_group_config: normalizedGroups,
        video_type_ids: selectedVideoTypeIds,
      });
      setTemplates((current) =>
        current.map((item) => (item.id === updated.id ? updated : item))
      );
      setSelectedTemplate(null);
      setShowEditModal(false);
      showToast('模板配置已保存');
    } catch (error) {
      showToast(error.response?.data?.message || '保存模板配置失败', 'danger');
    } finally {
      setSavingId('');
    }
  };

  return (
    <>
      {pageBusy && (
        <div className="video-template-page-overlay" aria-busy="true">
          <LoadingIndicator containerClassName="flex-row" label={pageBusyLabel} />
        </div>
      )}

      <ToastContainer position="top-center" className="p-3">
        <Toast
          show={toastInfo.show}
          onClose={() => setToastInfo({ ...toastInfo, show: false })}
          delay={3000}
          autohide
          bg={toastInfo.variant}
        >
          <Toast.Body
            className={
              toastInfo.variant === 'warning' || toastInfo.variant === 'light'
                ? 'text-dark'
                : 'text-white'
            }
          >
            {toastInfo.message}
          </Toast.Body>
        </Toast>
      </ToastContainer>

      <PageContentLayout
        pageClassName="video-templates-page"
        title="自动化模板管理"
        actions={
          <>
            <Button
              variant="primary"
              onClick={() => setShowCreateModal(true)}
              disabled={pageBusy}
            >
              创建高级模板
            </Button>
            <Button
              variant="outline-primary"
              onClick={() => setShowSyncModal(true)}
              disabled={pageBusy}
            >
              同步普通模板
            </Button>
          </>
        }
        bodyClassName="templates-list"
      >
        {loading ? (
          <div className="templates-loading">
            <Spinner animation="border" size="sm" />
            <span>加载中</span>
          </div>
        ) : (
          <Table bordered hover responsive>
            <thead>
              <tr>
                <th>展示名称</th>
                <th>模板类型</th>
                <th>状态</th>
                <th>启用</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {templates.map((template) => (
                <tr key={template.id}>
                  <td>{template.name}</td>
                  <td>{getVideoTemplateTypeLabel(template.template_type)}</td>
                  <td>{getVideoTemplateStatusLabel(template.status)}</td>
                  <td className="template-active-cell">
                    <Form.Check
                      type="switch"
                      id={`template-active-${template.id}`}
                      aria-label={`切换模板 ${template.name || template.id} 启用状态`}
                      checked={template.is_active !== false}
                      onChange={() => handleToggleTemplateActive(template)}
                      disabled={pageBusy}
                    />
                  </td>
                  <td>
                    {shouldShowSyncOrdinaryTemplateButton(template) && (
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="me-2"
                        disabled={pageBusy}
                        onClick={() => handleSyncTemplate(template)}
                      >
                        {syncingId === template.id ? '同步中' : '同步阿里模板'}
                      </Button>
                    )}
                    {shouldShowRefreshAdvancedTemplateButton(template) && (
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="me-2"
                        disabled={pageBusy}
                        onClick={() => handleSyncTemplate(template)}
                      >
                        {syncingId === template.id ? '刷新中' : '刷新状态'}
                      </Button>
                    )}
                    {shouldShowParseSlotsButton(template) && (
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="me-2"
                        disabled={pageBusy}
                        onClick={() => handleParseSlots(template)}
                      >
                        {parsingId === template.id ? '解析中' : '解析插槽'}
                      </Button>
                    )}
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="me-2"
                      disabled={pageBusy}
                      onClick={() => handleSelectTemplate(template)}
                    >
                      编辑
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      disabled={pageBusy}
                      onClick={() => setDeleteCandidate(template)}
                    >
                      {deletingId === template.id ? '删除中' : '删除'}
                    </Button>
                  </td>
                </tr>
              ))}
              {templates.length === 0 && (
                <tr>
                  <td colSpan="5" className="text-center">
                    暂无模板
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        )}
      </PageContentLayout>

      <Modal
        show={showCreateModal}
        onHide={closeCreateModal}
        backdrop={pageBusy ? 'static' : true}
        keyboard={!pageBusy}
        centered
        className="video-template-modal"
      >
        <Form onSubmit={handleCreateTemplate}>
          <Modal.Header closeButton={!pageBusy}>
            <Modal.Title>创建高级模板</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="template-create-form">
              <Form.Group className="mb-3">
                <Form.Label>模板名称</Form.Label>
                <Form.Control
                  name="name"
                  value={createForm.name}
                  onChange={handleCreateChange}
                  disabled={pageBusy}
                  required
                />
              </Form.Group>
              <Form.Group className="mb-0">
                <Form.Label>模板 ZIP</Form.Label>
                <Form.Control
                  name="file"
                  type="file"
                  accept=".zip,application/zip"
                  onChange={handleCreateChange}
                  disabled={pageBusy}
                  required
                />
              </Form.Group>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" type="button" onClick={closeCreateModal} disabled={pageBusy}>
              取消
            </Button>
            <Button variant="primary" type="submit" disabled={pageBusy}>
              {creating ? '创建中' : '创建高级模板'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal
        show={showSyncModal}
        onHide={closeSyncModal}
        backdrop={pageBusy ? 'static' : true}
        keyboard={!pageBusy}
        centered
        className="video-template-modal"
      >
        <Form onSubmit={handleSyncOrdinaryTemplate}>
          <Modal.Header closeButton={!pageBusy}>
            <Modal.Title>同步普通模板</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-0">
              <Form.Label>阿里云普通模板 ID</Form.Label>
              <Form.Control
                name="remote_template_id"
                value={syncForm.remote_template_id}
                onChange={handleSyncChange}
                disabled={pageBusy}
                required
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" type="button" onClick={closeSyncModal} disabled={pageBusy}>
              取消
            </Button>
            <Button variant="primary" type="submit" disabled={pageBusy}>
              {syncingId === 'ordinary-template-sync' ? '同步中' : '同步普通模板'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal
        show={!!deleteCandidate}
        onHide={closeDeleteModal}
        backdrop={pageBusy ? 'static' : true}
        keyboard={!pageBusy}
        centered
        className="video-template-modal"
      >
        <Modal.Header closeButton={!pageBusy}>
          <Modal.Title>删除模板</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            确认删除模板“{deleteCandidate?.name || '-'}”？
          </p>
          <p className="text-muted mb-0">
            仅本地归档模板，不删除阿里云远端模板。
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeDeleteModal} disabled={pageBusy}>
            取消
          </Button>
          <Button
            variant="danger"
            disabled={pageBusy}
            onClick={handleDeleteTemplate}
          >
            {deletingId ? '删除中' : '确认删除'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showEditModal && !!selectedTemplate}
        onHide={closeEditModal}
        backdrop={pageBusy ? 'static' : true}
        keyboard={!pageBusy}
        size="xl"
        centered
        scrollable
        className="video-template-modal"
        dialogClassName={TEMPLATE_EDIT_DIALOG_CLASS}
      >
        {selectedTemplate && (
          <>
            <Modal.Header closeButton={!pageBusy}>
              <Modal.Title>编辑模板</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="slot-editor">
                <div className="slot-editor-header">
                  <div>
                    <h5>模板配置</h5>
                    <div className="template-meta">
                      <span>类型: {getVideoTemplateTypeLabel(selectedTemplate.template_type)}</span>
                      <span>状态: {selectedStatus}</span>
                    </div>
                  </div>
                </div>

                <div className="slot-editor-basic">
                  <Form.Group>
                    <Form.Label>展示名称</Form.Label>
                    <Form.Control
                      name="name"
                      value={editForm.name}
                      onChange={handleEditChange}
                      disabled={pageBusy}
                    />
                  </Form.Group>
                  <Form.Check
                    type="switch"
                    id="selected-template-is-active"
                    name="is_active"
                    label="启用"
                    checked={editForm.is_active}
                    onChange={handleEditChange}
                    disabled={pageBusy}
                  />
                </div>

                <div className="template-video-type-binding">
                  <h5>适用视频标准</h5>
                  <div className="template-video-type-options">
                    {videoTypes.map((videoType) => {
                      const videoTypeId = Number(videoType.id);
                      return (
                        <Form.Check
                          key={videoType.id}
                          type="checkbox"
                          id={`template-video-type-${videoType.id}`}
                          label={getVideoTypeLabel(videoType)}
                          checked={selectedVideoTypeIds.includes(videoTypeId)}
                          onChange={() => handleVideoTypeToggle(videoTypeId)}
                          disabled={pageBusy}
                        />
                      );
                    })}
                    {videoTypes.length === 0 && (
                      <span className="template-video-type-empty">
                        暂无可绑定视频标准
                      </span>
                    )}
                  </div>
                </div>

                <div className="slot-editor-toolbar">
                  <h5>插槽配置</h5>
                  <div className="slot-editor-toolbar-actions">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      disabled={pageBusy || !canMergeSelectedSlots}
                      onClick={handleMergeSelectedSlots}
                    >
                      {mergeSelectedSlotsLabel}
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      disabled={pageBusy || !canSplitSelectedGroups}
                      onClick={handleSplitSelectedGroups}
                    >
                      {SLOT_EDITOR_ACTION_TEXT.SPLIT_SELECTED_GROUPS}
                    </Button>
                    {shouldShowSyncOrdinaryTemplateButton(selectedTemplate) && (
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        disabled={pageBusy}
                        onClick={() => handleSyncTemplate(selectedTemplate)}
                      >
                        {syncingId === selectedTemplate.id ? '同步中' : '同步阿里模板'}
                      </Button>
                    )}
                    {shouldShowRefreshAdvancedTemplateButton(selectedTemplate) && (
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        disabled={pageBusy}
                        onClick={() => handleSyncTemplate(selectedTemplate)}
                      >
                        {syncingId === selectedTemplate.id ? '刷新中' : '刷新状态'}
                      </Button>
                    )}
                    {shouldShowParseSlotsButton(selectedTemplate) && (
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        disabled={pageBusy}
                        onClick={() => handleParseSlots(selectedTemplate)}
                      >
                        {parsingId === selectedTemplate.id ? '解析中' : '解析插槽'}
                      </Button>
                    )}
                  </div>
                </div>

                <VerticalSortableList
                  disabled={pageBusy}
                  itemIds={slotSortableIds}
                  onMove={handleSlotMove}
                >
                  <VerticalSortableScrollArea
                    className="slot-editor-scroll"
                  >
                    <Table bordered hover className="slot-editor-table">
                      <thead>
                        <tr>
                          <th className="slot-select-cell">
                            <span className="visually-hidden">选择</span>
                          </th>
                          <th>插槽 Key</th>
                          <th>默认内容</th>
                          <th>配置</th>
                          <th className="slot-sort-cell">
                            <span className="visually-hidden">排序</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {slotEditorEntries.map((entry) =>
                          entry.type === TEMPLATE_SLOT_EDITOR_ENTRY_TYPE.GROUP ? (
                            <SortableGroupRow
                              key={entry.id}
                              disabled={pageBusy}
                              expanded={entry.expanded}
                              fontOptions={fontOptions}
                              group={entry.group}
                              groupKey={entry.groupKey}
                              selected={selectedGroupKeySet.has(entry.groupKey)}
                              onConfigChange={handleSlotGroupConfigChange}
                              onFieldChange={handleSlotGroupFieldChange}
                              onGroupSelectChange={handleGroupSelectionChange}
                              onRuntimeChange={handleSlotGroupRuntimeChange}
                              onToggleExpanded={handleToggleGroupExpanded}
                              slotCount={entry.slotCount}
                              sortableId={entry.id}
                            />
                          ) : (
                            <SortableSlotRow
                              key={entry.id}
                              disabled={pageBusy}
                              fontOptions={fontOptions}
                              insideGroup={entry.insideGroup}
                              index={entry.index}
                              onSlotConfigChange={handleSlotConfigChange}
                              onSlotRuntimeChange={handleSlotRuntimeChange}
                              onSlotSelectChange={handleSlotSelectionChange}
                              selected={selectedSlotKeySet.has(entry.slot.key)}
                              slot={entry.slot}
                              sortableId={entry.id}
                              onSlotChange={handleSlotChange}
                            />
                          )
                        )}
                        {slotRows.length === 0 && Object.keys(slotGroupConfig).length === 0 && (
                          <tr>
                            <td colSpan={SLOT_TABLE_COLUMN_COUNT} className="text-center">
                              暂无插槽
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </VerticalSortableScrollArea>
                </VerticalSortableList>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={closeEditModal}
                disabled={pageBusy}
              >
                取消
              </Button>
              <Button
                variant="primary"
                disabled={pageBusy}
                onClick={handleSaveConfig}
              >
                {savingId === selectedTemplate.id ? '保存中' : '保存配置'}
              </Button>
            </Modal.Footer>
          </>
        )}
      </Modal>
    </>
  );
};

export default VideoTemplatesPage;
